import logError from 'src/data/services/logError';
import React, { useState } from 'react';
import { Keyboard, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import FormField from 'src/presentation/components/atoms/FormField';
import SubmitButton from 'src/presentation/components/atoms/SubmitButton';
import Translations from 'src/core/utils/Translations';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import type { SettingsStackParamList } from 'src/presentation/navigation/types/NavigationTypes';

const isIos = Platform.OS === 'ios';

interface ProfileErrors {
	voornaam?: string;
	achternaam?: string;
}

function EditProfileForm() {
	const authStore = useAuthenticationStore();
	const navigation = useNavigation<NativeStackNavigationProp<SettingsStackParamList>>();
	const { userProfile } = authStore;

	const [voornaam, setVoornaam] = useState<string>(userProfile?.firstname ?? '');
	const [achternaam, setAchternaam] = useState<string>(userProfile?.lastname ?? '');
	const [errors, setErrors] = useState<ProfileErrors>({});

	const clearError = (field: keyof ProfileErrors) => {
		setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
	};

	const onSubmit = () => {
		Keyboard.dismiss();

		// Required fields are checked locally first; the API call only follows for a valid form.
		const nextErrors: ProfileErrors = {};
		if (!voornaam.trim()) nextErrors.voornaam = Translations.errorFieldRequired;
		if (!achternaam.trim()) nextErrors.achternaam = Translations.errorFieldRequired;
		setErrors(nextErrors);
		if (nextErrors.voornaam || nextErrors.achternaam) return;

		authStore.updateUser('', authStore.userProfile?.email ?? '', '', voornaam, achternaam).catch(logError);
	};

	return (
		<View>
			<FormField
				error={errors.voornaam}
				label={Translations.labelFirstName}
				placeholder={Translations.placeholderFirstNameShort}
				required={true}
				value={voornaam}
				onChangeText={(text) => {
					setVoornaam(text);
					clearError('voornaam');
				}}
			/>
			<FormField
				error={errors.achternaam}
				label={Translations.labelLastName}
				placeholder={Translations.placeholderLastNameShort}
				required={true}
				value={achternaam}
				onChangeText={(text) => {
					setAchternaam(text);
					clearError('achternaam');
				}}
			/>
			<FormField
				editable={false}
				hint={Translations.textEmailNotEditable}
				label={Translations.labelEmail}
				value={userProfile?.email ?? ''}
			/>

			{/* Changing the password is its own flow on a separate screen instead of fields in this form. */}
			<TouchableOpacity style={styles.passwordRow} onPress={() => navigation.navigate(Constants.screenUpdatePassword)}>
				<Ionicons name="lock-closed-outline" size={22} style={styles.passwordIcon} />
				<Text style={styles.passwordLabel}>{Translations.titleChangePassword}</Text>
				<Ionicons name="chevron-forward" size={22} style={styles.passwordChevron} />
			</TouchableOpacity>

			<SubmitButton
				isLoading={authStore.isLoading}
				label={Translations.buttonSave}
				style={isIos ? styles.submitIos : styles.submitAndroid}
				onPress={onSubmit}
			/>
		</View>
	);
}

export default EditProfileForm;

const styles = StyleSheet.create({
	passwordRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginHorizontal: Dimens.margin16,
		paddingHorizontal: Dimens.padding8,
		paddingVertical: Dimens.padding16,
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
	},
	passwordIcon: {
		color: Colors.secondary,
		marginRight: Dimens.margin12,
	},
	passwordLabel: {
		flex: 1,
		fontSize: Dimens.fontSize18,
		fontFamily: Fonts.montserratRegular,
		color: Colors.black,
	},
	passwordChevron: {
		color: Colors.lightGrey,
	},
	submitIos: {
		marginTop: Dimens.margin32,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	submitAndroid: {
		flexDirection: 'row',
		justifyContent: 'center',
		marginTop: Dimens.margin20,
	},
});
